import type { ReactNode } from 'react';
import { getObjectId, type Id, type Order } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useInvoiceOrdersApi } from '@hero/api-hooks';
import { SharedList } from '../../../app/List/List';
import { OrderCard } from '../../Order/Card/OrderCard';

export interface InvoiceOrdersTabProps {
  /** The invoice ID to fetch orders for */
  invoiceId: Id;
  /**
   * Bilateral viewer role on the parent invoice — `'buyer'` shows
   * the buy-side price (what the viewer pays/owes), `'seller'`
   * shows the sell-side price (what the viewer is owed).
   */
  asRole: 'buyer' | 'seller';
  /** Optional custom render for each order row */
  renderOrder?: (order: Order) => ReactNode;
  /** Optional click handler when an order card is clicked */
  onOrderClick?: (order: Order) => void;
}

export function InvoiceOrdersTab({
  invoiceId,
  asRole,
  renderOrder,
  onOrderClick,
}: InvoiceOrdersTabProps) {
  const { t } = useTranslation(['m-payment']);

  const query = useInvoiceOrdersApi(
    { _id: getObjectId(invoiceId) },
    { enabled: !!invoiceId },
  );

  return (
    <SharedList<Order>
      query={query}
      emptyMessage={t('m-payment:invoice.drawer.orders.no_orders')}
      className="gap-3 p-4"
      renderItem={order =>
        renderOrder ? (
          renderOrder(order)
        ) : (
          <OrderCard
            order={order}
            displayForUser={asRole === 'buyer' ? 'customer' : 'carrier'}
            onClick={onOrderClick}
          />
        )
      }
    />
  );
}
